import { useNavigate } from "@tanstack/solid-router";
import { Show } from "solid-js";
import { TICKET_PURCHASE_LABEL, TICKET_PURCHASE_URL } from "~/lib/tickets";

type Props = {
  eventId?: string;
  cityLabel?: string;
  connectOpen?: boolean;
};

export function TicketClaimGate(props: Props) {
  const navigate = useNavigate();
  const city = () => props.cityLabel ?? "your conference";
  const windowClosed = () => !!props.eventId && props.connectOpen === false;

  return (
    <div class="claim-gate card">
      <p class="status-chip">{windowClosed() ? "Window closed" : "Ticket required"}</p>
      <Show
        when={windowClosed()}
        fallback={
          <>
            <h1>Link your conference ticket</h1>
            <p class="muted">
              <Show
                when={props.eventId}
                fallback="Connect is for DataEngBytes ticket holders. Pick an open city on Home to link your ticket, then browse everyone who has opted in."
              >
                Connect is for DataEngBytes ticket holders. Link your {city()} ticket to browse the
                directory, connect with attendees, and scan badges.
              </Show>
            </p>
            <p class="muted">
              You can still build a draft profile while you sort out your ticket — it won't appear in
              the directory until your ticket is verified.
            </p>
          </>
        }
      >
        <h1>Claiming for this city has closed</h1>
        <p class="muted">
          The {city()} Connect window is no longer open for new ticket claims. If you already claimed
          any DataEngBytes ticket, you can still browse the directory. Otherwise pick an open city
          on Home.
        </p>
      </Show>

      <div class="claim-gate-actions">
        <Show when={props.eventId && !windowClosed()}>
          <button
            type="button"
            class="btn btn-primary"
            onClick={() =>
              navigate({ to: "/onboarding/$eventId", params: { eventId: props.eventId! } })
            }
          >
            Link my ticket
          </button>
        </Show>
        <Show when={!props.eventId || windowClosed()}>
          <button type="button" class="btn btn-primary" onClick={() => navigate({ to: "/" })}>
            Choose a conference
          </button>
          <Show when={windowClosed()}>
            <button type="button" class="btn btn-outline" onClick={() => navigate({ to: "/directory" })}>
              Browse all attendees
            </button>
          </Show>
        </Show>
        <a
          class="btn btn-outline"
          href={TICKET_PURCHASE_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Purchase a conference ticket
        </a>
        <Show when={props.eventId && !windowClosed()}>
          <button
            type="button"
            class="btn btn-ghost"
            onClick={() =>
              navigate({ to: "/directory/$eventId/edit", params: { eventId: props.eventId! } })
            }
          >
            Edit draft profile
          </button>
        </Show>
        <Show when={!props.eventId || windowClosed()}>
          <button type="button" class="btn btn-ghost" onClick={() => navigate({ to: "/me/edit" })}>
            Set up your profile
          </button>
        </Show>
      </div>

      <p class="claim-gate-foot muted">
        Purchase a conference ticket at{" "}
        <a href={TICKET_PURCHASE_URL} target="_blank" rel="noopener noreferrer">
          {TICKET_PURCHASE_LABEL}
        </a>
        . Step-by-step: <a href="/help#claim">how to claim</a> · <a href="/help">FAQ</a>.
      </p>
    </div>
  );
}
